/**
 * @file homeCases.js
 * @description Helpers for the homepage case card and live-case strip (ordering,
 *   "posted N ago", scannable facts). Ordering is newest first by created_at (the
 *   same field the card's "Just posted · N days ago" label reads, so order and
 *   label always agree). When the visitor's US state is known from coarse IP
 *   geolocation (see lib/visitorGeo.js), cases in that state lead, newest first,
 *   then every other case, newest first. Pure and stable: rows without a usable
 *   created_at keep the order the loader returned (already newest first).
 * @module lib/homeCases
 */

const ts = (c) => {
  const n = c?.created_at ? Date.parse(c.created_at) : NaN;
  return Number.isNaN(n) ? null : n;
};

const stateOf = (v) => String(v || "").trim().toUpperCase();

/**
 * @description Orders public open cases for the homepage: the visitor's state
 *   first (when known and at least one case is there), each group newest first.
 *   With no state, an unmatched state or garbage input this is plain newest first.
 * @param {Array<object>} cases - public open-case rows ({state, created_at?, ...})
 * @param {string|null} [visitorState] - 2-letter US state code, or null/unknown
 * @returns {Array<object>} new array; the input is not mutated
 */
export function sortCasesForVisitor(cases, visitorState = null) {
  if (!Array.isArray(cases)) return [];
  const want = /^[A-Z]{2}$/.test(stateOf(visitorState)) ? stateOf(visitorState) : null;
  return cases
    .map((c, i) => ({ c, i }))
    .sort((a, b) => {
      if (want) {
        const ma = stateOf(a.c?.state) === want ? 0 : 1;
        const mb = stateOf(b.c?.state) === want ? 0 : 1;
        if (ma !== mb) return ma - mb;
      }
      const ta = ts(a.c);
      const tb = ts(b.c);
      if (ta !== null && tb !== null && ta !== tb) return tb - ta;
      return a.i - b.i;
    })
    .map((x) => x.c);
}

// ── Card facts ──────────────────────────────────────────────────────────────
// Pure helpers behind the homepage case card. They read ONLY the public-safe
// columns the loader selects (see loadPublicOpenCases) and never touch client
// initials, agency identity, ZIP, address or free-text notes.

const DAY_ORDER = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/** A case counts as "just posted" for its first week; after that it is "posted N wk ago". */
export const FRESH_DAYS = 7;

/**
 * @description How long ago a case was posted, as a relativeTime locale key plus
 *   count. Returns null when the date is missing or unparseable, so the card
 *   never claims freshness it cannot back up.
 * @param {string} createdAt - ISO timestamp from cases.created_at
 * @param {number} [now] - epoch ms (injectable for tests)
 * @returns {{key: string, count?: number, fresh: boolean}|null}
 */
export function postedAgo(createdAt, now = Date.now()) {
  const ms = createdAt ? Date.parse(createdAt) : NaN;
  if (Number.isNaN(ms)) return null;
  const mins = Math.floor(Math.max(0, now - ms) / 60000);
  const hrs = Math.floor(mins / 60);
  const days = Math.floor(hrs / 24);
  if (mins < 2) return { key: "relativeTime.justNow", fresh: true };
  if (mins < 60) return { key: "relativeTime.minutesAgo", count: mins, fresh: true };
  if (hrs < 24) return { key: "relativeTime.hoursAgo", count: hrs, fresh: true };
  if (days < FRESH_DAYS) return { key: "relativeTime.daysAgo", count: days, fresh: true };
  return { key: "relativeTime.weeksAgo", count: Math.floor(days / 7), fresh: false };
}

// Client ages are shown as a range only. The loader returns age_min/age_max
// (already widened when the case was posted); a legacy single age_range value
// is widened the same way caseService.ageRangeDisplay does. An exact age is
// never produced.
function ageRangeText(row) {
  const lo = Number(row?.age_min);
  const hi = Number(row?.age_max);
  if (row?.age_min != null && row?.age_max != null && Number.isFinite(lo) && Number.isFinite(hi) && hi > lo) {
    return `${lo}–${hi}`;
  }
  const n = parseInt(row?.age_range, 10);
  if (!Number.isNaN(n) && n > 0) return `${Math.max(2, n - 1)}–${n + 1}`;
  return null;
}

function serviceDays(raw) {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    const days = Array.isArray(parsed?.days) ? parsed.days : [];
    return DAY_ORDER.filter((d) => days.includes(d));
  } catch {
    return []; // legacy free text is never surfaced
  }
}

/**
 * @description Compresses an ordered day list for display: a run of three or
 *   more consecutive days becomes {from, to}; anything else stays a list.
 * @param {string[]} days - canonical short days in Mon..Sun order
 * @returns {{range: {from: string, to: string}|null, list: string[]}}
 */
export function summarizeDays(days) {
  if (!Array.isArray(days) || days.length === 0) return { range: null, list: [] };
  const idx = days.map((d) => DAY_ORDER.indexOf(d));
  const contiguous = idx.every((v, i) => i === 0 || v === idx[i - 1] + 1);
  if (days.length >= 3 && contiguous) return { range: { from: days[0], to: days[days.length - 1] }, list: days };
  return { range: null, list: days };
}

/**
 * @description The scannable facts shown on the homepage case card, derived from
 *   a public open-case row. Every field is optional and omitted when the row
 *   does not carry it (no invented values).
 * @param {object} row - public open-case row
 * @returns {{hours: number|null, employment: "W2"|"1099"|"both"|null, ages: string|null,
 *   days: string[], perks: string[], settings: string[], delivery: "remote"|"hybrid"|null}}
 */
export function caseCardFacts(row) {
  const hoursNum = row?.hours == null || row.hours === "" ? NaN : Number(row.hours);
  const hours = Number.isFinite(hoursNum) && hoursNum > 0 ? Math.round(hoursNum * 10) / 10 : null;

  const emp = String(row?.employment || "").split(",").map((s) => s.trim().toUpperCase()).filter(Boolean);
  const has = (v) => emp.includes(v);
  const employment = has("W2") && has("1099") ? "both" : has("1099") ? "1099" : has("W2") ? "W2" : null;

  const perks = [];
  if (row?.training_provided === true) perks.push("training");
  if (row?.bcba_support === true) perks.push("bcba");
  if (row?.mileage_reimbursement === true) perks.push("mileage");

  const mode = String(row?.delivery_mode || "").toLowerCase();
  return {
    hours,
    employment,
    ages: ageRangeText(row),
    days: serviceDays(row?.service_time),
    perks,
    settings: String(row?.setting || "").split(",").map((s) => s.trim()).filter(Boolean),
    delivery: mode === "remote" || mode === "hybrid" ? mode : null,
  };
}

/** Public, shareable page for one open case (the existing /share/<id> route). */
export function publicCasePath(id) {
  return `/share/${encodeURIComponent(id)}`;
}
